import axios from 'axios';

// Create an axios instance with default config
export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://aigrower.iecosystems.ai/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // Increased to 30 seconds for slower connections
  withCredentials: false, // Set to false to avoid CORS issues
});

// Add request logging for debugging
const logRequest = (config) => {
  console.log(`API Request: ${config.method.toUpperCase()} ${config.url}`, {
    data: config.data,
    params: config.params,
    headers: config.headers
  });
  return config;
};

// Request interceptor for adding auth token and logging
api.interceptors.request.use(
  (config) => {
    // Add auth token if available - check for both regular and admin tokens
    const token = localStorage.getItem('authToken');
    const adminToken = localStorage.getItem('adminAuthToken');
    
    // Prioritize admin token if it exists (for admin operations)
    if (adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    } else if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log request for debugging
    return logRequest(config);
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => {
    // Log successful responses for debugging
    console.log(`API Response: ${response.status} ${response.config.method.toUpperCase()} ${response.config.url}`, {
      data: response.data
    });
    return response;
  },
  (error) => {
    // Enhanced error handling with more detailed logging
    if (error.response) {
      // Server responded with a status code outside of 2xx
      const { status, data, config } = error.response;
      console.error(`API Error ${status}: ${config.method.toUpperCase()} ${config.url}`, {
        data,
        requestData: config.data,
        requestParams: config.params
      });
      
      if (status === 401) {
        // Unauthorized - clear auth and redirect to login
        localStorage.removeItem('authToken');
        // In a real app, we would redirect to login page
        // window.location.href = '/login';
        console.warn('Authentication required. Clearing auth token.');
      }
      
      if (status === 403) {
        // Forbidden - user doesn't have permission
        console.error('Permission denied: You do not have access to this resource');
      }
      
      if (status === 500) {
        // Server error
        console.error('Server error occurred. Please try again or contact support.');
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('No response received from server. Check network connection and server status.', {
        request: error.request,
        config: error.config
      });
      
      // Attempt to retry the request if appropriate
      if (!error.config._retryCount || error.config._retryCount < 2) {
        error.config._retryCount = error.config._retryCount ? error.config._retryCount + 1 : 1;
        console.log(`Retrying request (attempt ${error.config._retryCount}/2)...`);
        return new Promise(resolve => setTimeout(() => resolve(api(error.config)), 1000));
      }
    } else {
      // Something happened in setting up the request
      console.error('Error setting up request:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Farm API services
export const farmService = {
  // Get all zones
  getZones: () => api.get('/api/farm/zones'),
  
  // Get a specific zone
  getZone: (zoneId) => api.get(`/api/farm/zones/${zoneId}`),
  
  // Create a new zone
  createZone: (zoneData) => api.post('/api/farm/zones', zoneData),
  
  // Update a zone
  updateZone: (zoneId, zoneData) => api.put(`/api/farm/zones/${zoneId}`, zoneData),
  
  // Delete a zone
  deleteZone: (zoneId) => api.delete(`/api/farm/zones/${zoneId}`),
  
  // Get metrics for a zone
  getZoneMetrics: (zoneId, metricType, timeRange) => 
    api.get(`/api/farm/zones/${zoneId}/metrics`, { params: { metricType, timeRange } }),
};

// Weather API services
export const weatherService = {
  // Get current weather
  getCurrentWeather: () => api.get('/api/weather/current'),
  
  // Get weather forecast
  getForecast: (days = 7) => api.get('/api/weather/forecast', { params: { days } }),
  
  // Get historical weather data
  getHistoricalWeather: (startDate, endDate) => 
    api.get('/api/weather/historical', { params: { startDate, endDate } }),
};

// Agent API services
export const agentService = {
  // Get recommendations
  getRecommendations: (zoneId) => 
    api.get('/agent/recommendations', { params: { zoneId } }),
  
  // Ask a question to the agent
  askQuestion: (question, zoneId) => 
    api.post('/agent/message', { message: question, zoneId }),
  
  // Get task schedule
  getTaskSchedule: () => api.get('/agent/tasks'),
  
  // Create a new task
  createTask: (taskData) => api.post('/agent/tasks', taskData),
  
  // Update a task
  updateTask: (taskId, taskData) => api.put(`/agent/tasks/${taskId}`, taskData),
  
  // Delete a task
  deleteTask: (taskId) => api.delete(`/agent/tasks/${taskId}`),
  
  // Mark a task as complete
  completeTask: (taskId) => api.put(`/agent/tasks/${taskId}/complete`),
};

// Only export the api instance once
export default api;
